import { flatMap, hasProp, isPlainObject, pipe, unique } from 'remeda'

// Private types ---------------------------------------------------------------

type Cleanup = () => void

// Public types ----------------------------------------------------------------

export type GridRuntime = Readonly<{
  beginBackendUpdate: (rows: readonly unknown[]) => Cleanup
  isBackendUpdating: (rowId: unknown) => boolean
  scheduleFrame: (callback: () => void) => void
  cleanup: Cleanup
}>

// Constants -------------------------------------------------------------------

const BACKEND_UPDATE_RELEASE_DELAY = 100

// Private functions -----------------------------------------------------------

/** Provide a release callback when no backend update needs tracking. */
const noop = (): void => undefined

/** Collect unique row identities affected by a backend update. */
const backendRowIds = (rows: readonly unknown[]): readonly string[] =>
  pipe(
    rows,
    flatMap((row) => {
      if (!(isPlainObject(row) && hasProp(row, 'id'))) {
        return []
      }

      const id = normalizeGridRowId(row.id)
      return id === undefined ? [] : [id]
    }),
    unique()
  )

// Public functions ------------------------------------------------------------

/** Normalize supported row identities for per-grid update tracking. */
export const normalizeGridRowId = (value: unknown): string | undefined =>
  typeof value === 'string'
    ? value
    : typeof value === 'number' && Number.isFinite(value)
      ? String(value)
      : undefined

/** Own backend update suppression and deferred work for one grid lifetime. */
export const createGridRuntime = (): GridRuntime => {
  const backendUpdateCounts = new Map<string, number>()
  const releaseTimers = new Set<ReturnType<typeof setTimeout>>()
  const pendingFrames = new Set<number>()
  let active = true

  /** Release one suppression claim for each affected row. */
  const release = (ids: readonly string[]): void => {
    for (const id of ids) {
      const count = backendUpdateCounts.get(id)

      if (count === undefined || count <= 1) {
        backendUpdateCounts.delete(id)
      } else {
        backendUpdateCounts.set(id, count - 1)
      }
    }
  }

  return {
    beginBackendUpdate: (rows): Cleanup => {
      const ids = backendRowIds(rows)

      if (!(active && ids.length > 0)) {
        return noop
      }

      for (const id of ids) {
        backendUpdateCounts.set(id, (backendUpdateCounts.get(id) ?? 0) + 1)
      }

      let finished = false

      return () => {
        if (finished || !active) {
          return
        }

        finished = true
        const timer = setTimeout(() => {
          releaseTimers.delete(timer)

          if (active) {
            release(ids)
          }
        }, BACKEND_UPDATE_RELEASE_DELAY)

        releaseTimers.add(timer)
      }
    },
    isBackendUpdating: (rowId): boolean => {
      const id = normalizeGridRowId(rowId)
      return id !== undefined && backendUpdateCounts.has(id)
    },
    scheduleFrame: (callback): void => {
      if (!active) {
        return
      }

      const frame = requestAnimationFrame(() => {
        pendingFrames.delete(frame)

        if (active) {
          callback()
        }
      })

      pendingFrames.add(frame)
    },
    cleanup: () => {
      active = false

      for (const timer of releaseTimers) {
        clearTimeout(timer)
      }

      for (const frame of pendingFrames) {
        cancelAnimationFrame(frame)
      }

      releaseTimers.clear()
      pendingFrames.clear()
      backendUpdateCounts.clear()
    }
  }
}
